import React, { useEffect, useState } from 'react';
import api from '../../../services/api';

interface Props {
  dotNumber: string;
}

export const IntelligenceTab: React.FC<Props> = ({ dotNumber }) => {
  const [riskV2, setRiskV2] = useState<any>(null);
  const [lineage, setLineage] = useState<any>(null);
  const [weatherCrashes, setWeatherCrashes] = useState<any>(null);
  const [revocationRisk, setRevocationRisk] = useState<any>(null);
  const [insuranceLapses, setInsuranceLapses] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const endpoints = [
          `/carriers/${dotNumber}/risk-v2`,
          `/carriers/${dotNumber}/lineage`,
          `/carriers/${dotNumber}/weather-adjusted-crashes`,
          `/carriers/${dotNumber}/revocation-risk`,
          `/carriers/${dotNumber}/insurance-lapses`,
        ];
        const results = await Promise.allSettled(
          endpoints.map(ep => api.get(ep))
        );
        // The axios response interceptor in services/api.ts already unwraps the
        // { success, data } envelope, so result.value.data IS the payload.
        if (results[0].status === 'fulfilled') setRiskV2(results[0].value.data);
        if (results[1].status === 'fulfilled') setLineage(results[1].value.data);
        if (results[2].status === 'fulfilled') setWeatherCrashes(results[2].value.data);
        if (results[3].status === 'fulfilled') setRevocationRisk(results[3].value.data);
        if (results[4].status === 'fulfilled') setInsuranceLapses(results[4].value.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [dotNumber]);

  if (loading) return <div className="p-6 text-gray-500">Loading intelligence data...</div>;

  const riskColor = (level: string) => {
    switch(level) {
      case 'CRITICAL': return { color: '#b3412c', bg: '#f1e2da' };
      case 'HIGH': return { color: '#963524', bg: '#f1e2da' };
      case 'MODERATE': return { color: '#7d561c', bg: '#efe3c8' };
      default: return { color: '#4b5c3a', bg: '#e2e7d5' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '16px' }}>
      {/* Composite Risk Score v2 */}
      {riskV2 && (
        <div className="card-modern fade-in-up" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>🛡️ Composite Risk Score v2</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              fontSize: '36px', fontWeight: 'bold', padding: '8px 16px', borderRadius: '8px',
              color: riskColor(riskV2.riskLevel).color,
              background: riskColor(riskV2.riskLevel).bg
            }}>
              {riskV2.overallRisk}
            </div>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: riskColor(riskV2.riskLevel).color }}>{riskV2.riskLevel}</div>
              <div style={{ fontSize: '13px', color: '#857f73' }}>Confidence: {riskV2.confidence}% | Data completeness: {riskV2.dataCompleteness}%</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            {Object.entries(riskV2.components).map(([key, comp]: [string, any]) => (
              <div key={key} style={{ border: '1px solid #e2ddd1', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: '#a39d90', textTransform: 'uppercase' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{comp.score}</div>
                <div style={{ fontSize: '11px', color: '#a39d90' }}>weight: {(comp.weight * 100)}%</div>
                {comp.factors.map((f: string, i: number) => (
                  <div key={i} style={{ fontSize: '11px', color: '#b3412c', marginTop: '4px' }}>⚠ {f}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Revocation Risk */}
      {revocationRisk && (
        <div className="card-modern fade-in-up" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>🔮 Revocation Risk Prediction</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              fontSize: '28px', fontWeight: 'bold', padding: '4px 12px', borderRadius: '8px',
              color: riskColor(revocationRisk.riskLevel).color,
              background: riskColor(revocationRisk.riskLevel).bg
            }}>
              {revocationRisk.revocationProbability}%
            </div>
            <div>
              <div style={{ fontSize: '14px' }}>{revocationRisk.riskLevel} risk of revocation</div>
              <div style={{ fontSize: '12px', color: '#857f73' }}>Time horizon: {revocationRisk.timeHorizon}</div>
            </div>
          </div>
          {revocationRisk.features.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              {revocationRisk.features.map((f: any, i: number) => (
                <div key={i} style={{ fontSize: '13px', display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span>{f.name}: <strong>{f.value}</strong></span>
                  <span style={{ color: '#b3412c' }}>+{f.contribution} pts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Carrier Lineage */}
      {lineage && lineage.totalRelated > 0 && (
        <div className="card-modern fade-in-up" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>🌳 Carrier Lineage Tree</h3>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px' }}>
              Chameleon Score: <strong style={{ color: lineage.chameleonScore > 50 ? '#b3412c' : '#4b5c3a' }}>{lineage.chameleonScore}/100</strong>
            </span>
            <span style={{ fontSize: '14px', color: '#857f73' }}>{lineage.totalRelated} related carrier(s)</span>
          </div>
          {['ancestors', 'siblings', 'descendants'].map(group => (
            lineage[group]?.length > 0 ? (
              <div key={group} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#857f73', textTransform: 'uppercase', marginBottom: '4px' }}>{group}</div>
                {lineage[group].map((node: any, i: number) => (
                  <div key={i} style={{ borderLeft: '2px solid #a39d90', paddingLeft: '12px', paddingTop: '4px', paddingBottom: '4px', marginLeft: '8px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>{node.legalName} <span style={{ color: '#a39d90' }}>DOT {node.dotNumber}</span></div>
                    <div style={{ fontSize: '12px', color: '#857f73' }}>Confidence: {node.confidence}% | {node.status}</div>
                    <div style={{ fontSize: '12px', color: '#34312c' }}>{node.signals.join(' • ')}</div>
                  </div>
                ))}
              </div>
            ) : null
          ))}
        </div>
      )}

      {/* Weather-Adjusted Crashes */}
      {weatherCrashes && weatherCrashes.totalCrashes > 0 && (
        <div className="card-modern fade-in-up" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>🌧️ Weather-Adjusted Crash Analysis</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{weatherCrashes.totalCrashes}</div>
              <div style={{ fontSize: '11px', color: '#857f73' }}>Total Crashes</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{weatherCrashes.rawSeverityTotal}</div>
              <div style={{ fontSize: '11px', color: '#857f73' }}>Raw Severity</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{weatherCrashes.adjustedSeverityTotal}</div>
              <div style={{ fontSize: '11px', color: '#857f73' }}>Adjusted Severity</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4b5c3a' }}>-{weatherCrashes.severityReduction}%</div>
              <div style={{ fontSize: '11px', color: '#857f73' }}>Condition Adj.</div>
            </div>
          </div>
          <div style={{ fontSize: '13px', color: '#857f73' }}>
            {weatherCrashes.adverseConditionPct}% of crashes occurred in adverse conditions
          </div>
        </div>
      )}

      {/* Insurance Lapse Detection */}
      {insuranceLapses && insuranceLapses.totalFilings > 0 && (
        <div className="card-modern fade-in-up" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>🏦 Insurance Lapse Analysis</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              fontSize: '28px', fontWeight: 'bold', padding: '4px 12px', borderRadius: '8px',
              color: riskColor(insuranceLapses.lapseRiskLevel).color,
              background: riskColor(insuranceLapses.lapseRiskLevel).bg
            }}>
              {insuranceLapses.lapseRiskScore}
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: riskColor(insuranceLapses.lapseRiskLevel).color }}>{insuranceLapses.lapseRiskLevel} Lapse Risk</div>
              <div style={{ fontSize: '12px', color: '#857f73' }}>{insuranceLapses.totalFilings} filings | {insuranceLapses.uniqueInsurers.length} insurers | {insuranceLapses.cancellationCount} cancellations</div>
            </div>
          </div>
          {insuranceLapses.riskIndicators.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {insuranceLapses.riskIndicators.map((r: string, i: number) => (
                <div key={i} style={{ fontSize: '13px', color: '#b3412c', marginBottom: '4px' }}>⚠ {r}</div>
              ))}
            </div>
          )}
          {insuranceLapses.lapses.length > 0 && (
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Coverage Gaps:</div>
              {insuranceLapses.lapses.slice(0, 5).map((l: any, i: number) => (
                <div key={i} style={{ fontSize: '12px', padding: '6px', background: '#f1e2da', borderRadius: '4px', marginBottom: '4px' }}>
                  <strong>{l.gapDays} days</strong> gap ({l.gapStartDate} → {l.gapEndDate}) — {l.previousInsurer} → {l.nextInsurer}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!riskV2 && !lineage && !weatherCrashes && !revocationRisk && !insuranceLapses && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#a39d90' }}>
          No intelligence data available for this carrier.
        </div>
      )}
    </div>
  );
};

export default IntelligenceTab;
